import { pb } from '@/lib/pocketbase';

export interface HomepageSectionSettings {
  id?: string;
  sectionMode: 'tutors' | 'carousel';
  mobileHeroTutorId?: string;
}

export interface CarouselSlide {
  id: string;
  mediaType: 'image' | 'video';
  mediaFile?: string;
  mediaUrl?: string;
  headerText: string;
  captionText?: string;
  buttonText?: string;
  buttonLink?: string;
  displayOrder: number;
  enabled: boolean;
  collectionId?: string;
}

// Default settings when no collection exists
const DEFAULT_SETTINGS: HomepageSectionSettings = {
  sectionMode: 'tutors',
  mobileHeroTutorId: '',
};

export const homepageSectionService = {
  // Fetch current section settings
  async getSettings(): Promise<HomepageSectionSettings> {
    try {
      const result = await pb.collection('homepage_section_settings').getList(1, 1, {
        sort: '-updated',
      });
      
      if (result.items.length > 0) {
        const item = result.items[0];
        return {
          id: item.id,
          sectionMode: item.sectionMode || 'tutors',
          mobileHeroTutorId: item.mobileHeroTutorId || '',
        };
      }
      
      return DEFAULT_SETTINGS;
    } catch (error) {
      console.error('Error fetching homepage section settings:', error);
      return DEFAULT_SETTINGS;
    }
  },

  // Update section settings
  async updateSettings(settings: Partial<HomepageSectionSettings>): Promise<HomepageSectionSettings> {
    try {
      // Try to get existing record
      const existing = await pb.collection('homepage_section_settings').getList(1, 1);
      
      if (existing.items.length > 0) {
        const updated = await pb.collection('homepage_section_settings').update(existing.items[0].id, settings);
        return {
          id: updated.id,
          sectionMode: updated.sectionMode || 'tutors',
          mobileHeroTutorId: updated.mobileHeroTutorId || '',
        };
      } else {
        // Create new record
        const created = await pb.collection('homepage_section_settings').create({
          ...DEFAULT_SETTINGS,
          ...settings,
        });
        return {
          id: created.id,
          sectionMode: created.sectionMode || 'tutors',
          mobileHeroTutorId: created.mobileHeroTutorId || '',
        };
      }
    } catch (error) {
      console.error('Error updating homepage section settings:', error);
      throw error;
    }
  },

  // Fetch all carousel slides
  async getCarouselSlides(): Promise<CarouselSlide[]> {
    try {
      const result = await pb.collection('homepage_carousel_slides').getFullList({
        filter: 'enabled = true',
        sort: 'displayOrder',
      });
      
      return result.map(item => ({
        id: item.id,
        mediaType: item.mediaType || 'image',
        mediaFile: item.mediaFile || '',
        mediaUrl: item.mediaUrl || '',
        headerText: item.headerText || '',
        captionText: item.captionText || '',
        buttonText: item.buttonText || '',
        buttonLink: item.buttonLink || '',
        displayOrder: item.displayOrder || 0,
        enabled: item.enabled !== false,
        collectionId: item.collectionId,
      }));
    } catch (error) {
      console.error('Error fetching carousel slides:', error);
      return [];
    }
  },

  // Fetch all slides for admin (including disabled)
  async getAllCarouselSlides(): Promise<CarouselSlide[]> {
    try {
      const result = await pb.collection('homepage_carousel_slides').getFullList({
        sort: 'displayOrder',
      });
      
      return result.map(item => ({
        id: item.id,
        mediaType: item.mediaType || 'image',
        mediaFile: item.mediaFile || '',
        mediaUrl: item.mediaUrl || '',
        headerText: item.headerText || '',
        captionText: item.captionText || '',
        buttonText: item.buttonText || '',
        buttonLink: item.buttonLink || '',
        displayOrder: item.displayOrder || 0,
        enabled: item.enabled !== false,
        collectionId: item.collectionId,
      }));
    } catch (error) {
      console.error('Error fetching all carousel slides:', error);
      return [];
    }
  },

  // Create a new carousel slide
  async createSlide(slide: Omit<CarouselSlide, 'id'>): Promise<CarouselSlide> {
    try {
      const created = await pb.collection('homepage_carousel_slides').create(slide);
      return {
        id: created.id,
        mediaType: created.mediaType,
        mediaFile: created.mediaFile,
        mediaUrl: created.mediaUrl,
        headerText: created.headerText,
        captionText: created.captionText,
        buttonText: created.buttonText,
        buttonLink: created.buttonLink,
        displayOrder: created.displayOrder,
        enabled: created.enabled,
        collectionId: created.collectionId,
      };
    } catch (error) {
      console.error('Error creating carousel slide:', error);
      throw error;
    }
  },

  // Update a carousel slide
  async updateSlide(id: string, slide: Partial<CarouselSlide>): Promise<CarouselSlide> {
    try {
      const updated = await pb.collection('homepage_carousel_slides').update(id, slide);
      return {
        id: updated.id,
        mediaType: updated.mediaType,
        mediaFile: updated.mediaFile,
        mediaUrl: updated.mediaUrl,
        headerText: updated.headerText,
        captionText: updated.captionText,
        buttonText: updated.buttonText,
        buttonLink: updated.buttonLink,
        displayOrder: updated.displayOrder,
        enabled: updated.enabled,
        collectionId: updated.collectionId,
      };
    } catch (error) {
      console.error('Error updating carousel slide:', error);
      throw error;
    }
  },

  // Delete a carousel slide
  async deleteSlide(id: string): Promise<void> {
    try {
      await pb.collection('homepage_carousel_slides').delete(id);
    } catch (error) {
      console.error('Error deleting carousel slide:', error);
      throw error;
    }
  },

  // Upload media file for a slide
  async uploadSlideMedia(slideId: string, file: File): Promise<CarouselSlide> {
    try {
      const formData = new FormData();
      formData.append('mediaFile', file);
      
      const updated = await pb.collection('homepage_carousel_slides').update(slideId, formData);
      return {
        id: updated.id,
        mediaType: updated.mediaType,
        mediaFile: updated.mediaFile,
        mediaUrl: updated.mediaUrl,
        headerText: updated.headerText,
        captionText: updated.captionText,
        displayOrder: updated.displayOrder,
        enabled: updated.enabled,
        collectionId: updated.collectionId,
      };
    } catch (error) {
      console.error('Error uploading slide media:', error);
      throw error;
    }
  },

  // Get media URL for a slide
  getSlideMediaUrl(slide: CarouselSlide): string {
    if (slide.mediaUrl) {
      return slide.mediaUrl;
    }
    if (slide.mediaFile && slide.collectionId) {
      return `${pb.baseUrl}/api/files/homepage_carousel_slides/${slide.id}/${slide.mediaFile}`;
    }
    return '';
  },

  // Check if URL is a YouTube link
  isYouTubeUrl(url: string): boolean {
    return url.includes('youtube.com') || url.includes('youtu.be');
  },

  // Extract YouTube video ID
  getYouTubeVideoId(url: string): string | null {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  },
};
